import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { useUsers } from '../../hooks/useUsers';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import { formatDateShort } from '../../utils/date';
import type { User } from '../../api/users';

const columnHelper = createColumnHelper<User>();

export default function UsersListPage() {
  const navigate = useNavigate();
  const { data: users, isLoading } = useUsers();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{info.getValue()}</span>,
      }),
      columnHelper.accessor('email', {
        header: 'Email',
        cell: (info) => <span className="text-sm text-gray-600 dark:text-gray-300">{info.getValue()}</span>,
      }),
      columnHelper.accessor('urn', {
        header: 'URN',
        cell: (info) => <span className="font-mono text-xs text-gray-500 dark:text-gray-400 truncate max-w-[250px] block">{info.getValue()}</span>,
      }),
      columnHelper.accessor('privileges', {
        header: 'Privileges',
        cell: (info) => <span className="text-sm text-gray-500 dark:text-gray-400">{info.getValue()?.length || 0}</span>,
      }),
      columnHelper.accessor('creationDate', {
        header: 'Created',
        cell: (info) => <span className="text-sm text-gray-500 dark:text-gray-400">{formatDateShort(info.getValue())}</span>,
      }),
    ],
    [],
  );

  return (
    <div>
      <PageHeader
        title="Users"
        description="Manage users, privileges, and entitlements"
        actions={
          <button onClick={() => navigate('/users/new')} className="btn-primary gap-1">
            <Plus className="h-4 w-4" />
            New User
          </button>
        }
      />
      <DataTable
        data={users || []}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(user) => navigate(`/users/${encodeURIComponent(user.urn)}`)}
      />
    </div>
  );
}
